import { useMemo } from 'react';
import { BASE_URL_ANALYTICS } from '@frontend/api-analytics';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { useShowMissedCallTextRoiWidget } from './use-show-missed-call-text-roi-widget';

type Payload = {
  end_date: string;
  location_id: string[];
  org_id: string;
  start_date: string;
};

type MissedCallTextROI = {
  count: number;
  date: string;
};

const api = async (payload: Payload) => {
  const response = await http.post<{ data: MissedCallTextROI[] }>(
    `${BASE_URL_ANALYTICS}/roi/getmissedcalltext`,
    payload
  );
  return response.data;
};

export const useFetchMissedCallTextROI = (payload: Payload) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const hasAccess = useShowMissedCallTextRoiWidget();

  const { data, isLoading } = useScopedQuery({
    queryKey: `missed-call-text-roi`,
    queryFn: () => api(payload),
    onError: () => {
      alert.error(t("Couldn't fetch the missed call text ROI. Please try again."));
    },
    enabled: hasAccess,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const total = useMemo(() => data?.reduce((sum, { count }) => sum + count, 0) || 0, [data]);

  return {
    data,
    total,
    isLoading,
  };
};
